import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import {
  addStateLicenseType,
  editStateLicenseType,
  licenseTypeSaving,
  rowForEditLicenseType,
} from "../atoms/atoms";
import useAddLicenseType from "../hooks/useAddLicenseType";
import useEditLicenseType from "../hooks/useUpdateLicenseType";
import { LicenseTypeInfo } from "../types";
import LicenseTypeDetails from "./LicenseTypeDetails";

export const LicenseTypeForm = () => {
  // atoms
  const [editData, setEditData] = useRecoilState(rowForEditLicenseType);
  const [isSaving, setIsSaving] = useRecoilState(licenseTypeSaving);
  const [isAddState, setIsAddState] = useRecoilState(addStateLicenseType);
  const [isEditState, setIsEditState] = useRecoilState(editStateLicenseType);

  // hooks
  const addLicenseType = useAddLicenseType();
  const updateLicenseType = useEditLicenseType();

  // local
  const [newData, setNewData] = useState<LicenseTypeInfo>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const queryClient = useQueryClient();

  //Edit Business License & Permit Initialization/

  // form methods
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      licenseTypeName: data.licenseTypeName.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  const handleSave = () => {
    if (newData) {
      if (isEditState) {
        updateLicenseType(newData);
      } else {
        addLicenseType(newData);
      }
    }
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New License Type" : "Edit License Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <LicenseTypeDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add License Type" : "Update License Type"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
